import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getApprovedMessages } from '../utils/storage';
import { format } from 'date-fns';

const { FiHeart, FiSearch, FiClock, FiUser, FiFilter, FiArrowUp, FiArrowDown, FiLoader } = FiIcons;

const GalleryPage = () => {
  const [allMessages, setAllMessages] = useState([]);
  const [displayMessages, setDisplayMessages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const messages = await getApprovedMessages();
        setAllMessages(messages);
        
        const sortedMessages = [...messages].sort((a, b) => 
          new Date(b.timestamp) - new Date(a.timestamp)
        );
        setDisplayMessages(sortedMessages);
      } catch (error) {
        console.error("Error fetching messages:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, []);

  useEffect(() => {
    let filtered = [...allMessages];
    
    if (searchTerm) {
      const lowercaseSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(msg => 
        (msg.content && msg.content.toLowerCase().includes(lowercaseSearch)) || 
        (msg.title && msg.title.toLowerCase().includes(lowercaseSearch)) ||
        (msg.id && msg.id.toLowerCase().includes(lowercaseSearch)) ||
        (!msg.isAnonymous && msg.sender && msg.sender.toLowerCase().includes(lowercaseSearch))
      );
    }
    
    switch(sortOrder) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        break;
      case 'a-z':
        filtered.sort((a, b) => {
          if (a.title && b.title) return a.title.localeCompare(b.title);
          if (a.title) return -1;
          if (b.title) return 1;
          return a.content.localeCompare(b.content);
        });
        break;
      case 'z-a':
        filtered.sort((a, b) => {
          if (a.title && b.title) return b.title.localeCompare(a.title);
          if (a.title) return 1;
          if (b.title) return -1;
          return b.content.localeCompare(a.content);
        });
        break;
      default:
        break;
    }
    
    setDisplayMessages(filtered);
  }, [searchTerm, sortOrder, allMessages]);

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const toggleFilter = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const handleSortChange = (order) => {
    setSortOrder(order);
    setIsFilterOpen(false);
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-6xl font-bold gradient-text mb-4">
            Kindness Gallery
          </h1>
          <h2 className="text-2xl font-bold mb-6 kaas-subtitle">
            KAAS – Kindness as a Service
          </h2>
          <div className="flex justify-center items-center space-x-3">
            <SafeIcon icon={FiLoader} className="text-2xl text-heading animate-spin" />
            <p className="text-xl text-text font-medium">Loading kindness messages...</p>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-12"
      >
        <h1 className="text-4xl md:text-6xl font-bold gradient-text mb-4">
          Kindness Gallery
        </h1>
        <h2 className="text-2xl font-bold mb-6 kaas-subtitle">
          KAAS – Kindness as a Service
        </h2>
        <p className="text-xl text-text mb-8 max-w-2xl mx-auto font-medium">
          Browse messages of kindness shared by our community. Find inspiration or simply enjoy the positivity.
        </p>
      </motion.div>

      {/* Search and Filter Controls */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="clay-card mb-8"
      >
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search messages, keywords, titles, or IDs..."
              className="clay-input w-full pl-12 pr-4 py-4 text-text placeholder-gray-500 font-medium focus-visible"
            />
            <SafeIcon 
              icon={FiSearch} 
              className="absolute left-4 top-1/2 transform -translate-y-1/2 text-xl text-text" 
            />
          </div>
          
          <div className="relative">
            <button
              type="button"
              onClick={toggleFilter}
              className="clay-button-yellow w-full sm:w-auto px-6 py-4 flex items-center justify-center space-x-3 font-bold hover:transform hover:scale-105 transition-all duration-300 focus-visible"
            >
              <SafeIcon icon={FiFilter} className="text-xl text-text" />
              <span>Sort By</span>
            </button>
            
            {isFilterOpen && (
              <div className="absolute right-0 mt-2 w-48 clay-card p-2 z-10">
                <button
                  type="button"
                  onClick={() => handleSortChange('newest')}
                  className={`w-full text-left p-3 rounded-lg mb-1 hover:bg-purple/20 transition-colors font-bold ${sortOrder === 'newest' ? 'bg-purple/30' : ''}`}
                >
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiArrowDown} className="text-lg text-text" />
                    <span>Newest First</span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => handleSortChange('oldest')}
                  className={`w-full text-left p-3 rounded-lg mb-1 hover:bg-purple/20 transition-colors font-bold ${sortOrder === 'oldest' ? 'bg-purple/30' : ''}`}
                >
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiArrowUp} className="text-lg text-text" />
                    <span>Oldest First</span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => handleSortChange('a-z')}
                  className={`w-full text-left p-3 rounded-lg mb-1 hover:bg-purple/20 transition-colors font-bold ${sortOrder === 'a-z' ? 'bg-purple/30' : ''}`}
                >
                  <div className="flex items-center space-x-3">
                    <span className="font-mono font-bold">A→Z</span>
                    <span>Alphabetical</span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => handleSortChange('z-a')}
                  className={`w-full text-left p-3 rounded-lg hover:bg-purple/20 transition-colors font-bold ${sortOrder === 'z-a' ? 'bg-purple/30' : ''}`}
                >
                  <div className="flex items-center space-x-3">
                    <span className="font-mono font-bold">Z→A</span>
                    <span>Reverse Alpha</span>
                  </div>
                </button>
              </div>
            )}
          </div>
        </form>
      </motion.div>

      {/* Messages Grid */}
      {displayMessages.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayMessages.map((message, index) => (
            <motion.div
              key={message.id}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.05 * (index % 6), duration: 0.5 }}
            >
              <Link
                to={`/message/${message.id}`}
                className="block hover:transform hover:scale-105 transition-all duration-300 focus-visible"
              >
                <div className="message-card">
                  <div className="message-card-header">
                    <SafeIcon icon={FiHeart} className="heart-logo text-xl" />
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiClock} className="text-sm text-text" />
                      <span className="text-sm font-bold text-text">
                        {format(new Date(message.timestamp), 'MMM d, yyyy')}
                      </span>
                    </div>
                  </div>
                  <div className="message-card-content">
                    {message.title && (
                      <h4 className="font-bold text-heading mb-3 text-lg">{message.title}</h4>
                    )}
                    <p className="message-text">"{message.content}"</p>
                  </div>
                  <div className="message-card-footer">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiUser} className="text-sm text-text" />
                      <span className="font-bold text-text">{message.isAnonymous ? 'Anonymous' : message.sender}</span>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="clay-card text-center"
        >
          <div className="clay-button-pink p-6 mx-auto mb-6 w-fit">
            <SafeIcon icon={FiHeart} className="text-4xl heart-logo" />
          </div>
          <h3 className="text-2xl font-bold text-heading mb-4">No Messages Found</h3>
          <p className="text-text font-medium mb-8">
            {searchTerm ? 'No messages match your search. Try different keywords.' : 'No kindness messages have been shared yet.'}
          </p>
          <Link
            to="/"
            className="clay-button-green px-8 py-4 inline-flex items-center space-x-3 font-bold text-text hover:transform hover:scale-105 transition-all duration-300 focus-visible"
          >
            <span className="text-lg">Send Your Own Kindness</span>
          </Link>
        </motion.div>
      )}

      {displayMessages.length > 0 && (
        <div className="text-center mt-12">
          <Link
            to="/"
            className="clay-button-pink px-10 py-5 inline-flex items-center space-x-3 font-bold text-text hover:transform hover:scale-105 transition-all duration-300 focus-visible"
          >
            <SafeIcon icon={FiHeart} className="text-2xl heart-logo" />
            <span className="text-xl">Send Your Own Kindness</span>
          </Link>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;